import {
	ChangeEvent,
	FunctionComponent,
	MouseEvent as ReactMouseEvent,
	useMemo,
	useState,
} from "react";

import { CircularProgress, Link } from "@mui/material";

import { Tooltip } from "@/components/Tooltip/Tooltip";
import { InputField } from "@/components/InputField/InputField";
import { LinkedinIcon } from "@/components/LinkedinIcon/LinkedinIcon";
import { WebsiteIcon } from "@/components/WebsiteIcon/WebsiteIcon";
import { InfoCardPopover, InfoCardPopoverRow } from "@/components/InfoCardPopover";
import { useUpdateOrganizationMutation } from "@/pages/Private/redux/organization/organization.api";

import { ensureHttpProtocol } from "@/utils/url";

import { HideOnErrorImg } from "@/components/HideOnErrorImg/HideOnErrorImg";

import { CrmIcon } from "../../List/components/CrmIcon";
import { OrganizationInfo } from "../schema/contacts";

export const EditableOrganizationNameColumn: FunctionComponent<{
	row: OrganizationInfo;
	handleCopyClick: (value: string) => void;
	setShowEdit: ({ id, name }: { id: number; name: string }) => void;
	showEdit: { id: number; name: string } | undefined;
	orderTitle: string;
}> = ({ row, handleCopyClick, orderTitle, setShowEdit, showEdit }) => {
	const [value, setValue] = useState<string | undefined>(row?.name || "");
	const [updateOrganization, { isLoading }] = useUpdateOrganizationMutation();

	const currentCompanyUrl = useMemo(() => {
		const url = row.websiteUrl ?? row.primaryDomain ?? undefined;

		return url ? ensureHttpProtocol(url) : undefined;
	}, [row.websiteUrl, row.primaryDomain]);

	if (isLoading) {
		return <CircularProgress />;
	}

	return (
		<div className="flex w-full max-w-[270px] items-center">
			{row.logoUrl && (
				<HideOnErrorImg
					alt="logo"
					className="max-h-[24px] h-full w-[24px] rounded mr-1"
					referrerPolicy="no-referrer"
					src={row.logoUrl}
				/>
			)}
			<div className="ml-2 w-full flex flex-row justify-between">
				{showEdit && showEdit?.id === row.id && showEdit?.name === orderTitle ? (
					<div className="min-w-[260px] absolute left-0 top-0 z-10">
						<InputField
							autoFocus
							handleBlur={() => {
								setShowEdit({ id: 0, name: "" });
							}}
							handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
								setValue(event.target.value);
							}}
							name={orderTitle}
							value={value || ""}
							onKeyDown={async (e) => {
								if (e.key === "Enter" && value) {
									await updateOrganization({
										id: row.id,
										name: value,
									}).unwrap();
									setShowEdit({ id: 0, name: "" });
								}
							}}
						/>
					</div>
				) : (
					<Tooltip
						content={
							<InfoCardPopover
								kind="company"
								onCopy={handleCopyClick}
								rows={
									[
										{ label: "Name", value: row.name },
										{
											label: "Location",
											value: [row.city, row.state, row.country].filter(Boolean).join(", ") || null,
										},
										{
											label: "Employees",
											value: row.estimatedNumEmployees?.toLocaleString() ?? null,
										},
										{
											label: "Short description",
											value: row.shortDescription,
											long: true,
										},
									] satisfies InfoCardPopoverRow[]
								}
							/>
						}
						exitDelay={100}
					>
						<button
							className="cursor-pointer mr-1 min-w-0 flex-1 max-w-[140px] overflow-hidden block text-left"
							onClick={(e: ReactMouseEvent) => {
								e.preventDefault();
								e.stopPropagation();
								handleCopyClick(row.name || "");
							}}
						>
							<span
								className="block truncate rounded-[5px] px-[3px] py-[1px] -mx-[3px] -my-[1px] hover:bg-gray-50 transition-colors"
								onContextMenu={(e) => {
									e.stopPropagation();
									e.preventDefault();
									setShowEdit({ id: row.id, name: orderTitle });
								}}
							>
								{row.name || ""}
							</span>
						</button>
					</Tooltip>
				)}

				<div className="flex items-center">
					{row.crmOrganizations?.map((crmOrganization) => (
						<Tooltip
							key={crmOrganization.id}
							content={
								<div className="flex flex-col gap-2">
									<span>Account Owner: {crmOrganization.accountOwner}</span>
									<span>Reference ID: {crmOrganization.referenceId}</span>
								</div>
							}
							disabled={!crmOrganization.url}
						>
							{crmOrganization.url ? (
								<Link href={ensureHttpProtocol(crmOrganization.url ?? "")} target="_blank">
									<CrmIcon url={crmOrganization.url ?? ""} />
								</Link>
							) : (
								<CrmIcon url={crmOrganization.url ?? ""} />
							)}
						</Tooltip>
					))}

					{row.linkedinUrl ? (
						<Link href={ensureHttpProtocol(row.linkedinUrl)} target="_blank">
							<LinkedinIcon status="checked" />
						</Link>
					) : (
						<LinkedinIcon status="failed" />
					)}

					{currentCompanyUrl ? (
						<Link href={currentCompanyUrl || ""} target="_blank">
							<WebsiteIcon status="checked" />
						</Link>
					) : (
						<></>
					)}
				</div>
			</div>
		</div>
	);
};
